import HekaLoader from "@/assets/heka-loader.svg?react";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAccountStore } from "@/stores/account.store";
import { usePlanStore } from "@/stores/plans.store";
import { useRecentProductsStore } from "@/stores/recent-products.store";
import { useSubscriptionStore } from "@/stores/subscriptions.store";
import { Plan, PlanActionsEnum } from "@/types/plan.type";
import { CreditCard, InfoIcon, SquareArrowOutUpRightIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

export function OneProductPage() {
  const { planSnapshot, planProxy } = usePlanStore();
  const params = useParams();
  const { accountSnapshot, accountProxy } = useAccountStore();
  const { subscriptionSnapshot, subscriptionProxy } = useSubscriptionStore();
  const { recentProductsStore } = useRecentProductsStore();
  const [hasPaymentMethods, setHasPaymentMethods] = useState<boolean | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isTermAccepted, setIsTermAccepted] = useState(false);
  const [open, setOpen] = useState(false);

  const subscribeHandler = async () => {
    const paymentMethods = await accountSnapshot.getPaymentMethods();
    setHasPaymentMethods(paymentMethods.length > 0);

    if (paymentMethods.length == 0) {
      accountProxy.setupPaymentMethod(
        `/products/${planSnapshot.currentPlan?.code}`,
        PlanActionsEnum.enum.subscribe
      );
      return;
    }
    subscriptionProxy.subscribe(
      planSnapshot.currentPlan?.code,
      accountSnapshot.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]
    );
    setOpen(false);
  };

  const checkPaymentMethods = useCallback(async () => {
    try {
      const paymentMethods = await accountSnapshot.getPaymentMethods();
      setHasPaymentMethods(paymentMethods.length > 0);
    } catch (error) {
      console.error("Error checking payment methods:", error);
      setHasPaymentMethods(false);
    }
  }, [accountSnapshot, setHasPaymentMethods]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const planCode = params.code || "";
        const resourceOwnerId =
          accountSnapshot.userInfo?.profile?.[
            "urn:zitadel:iam:user:resourceowner:id"
          ];

        if (resourceOwnerId) {
          await planProxy.getPlan(planCode, resourceOwnerId);
        } else {
          console.warn(
            "User resource owner ID not available, cannot fetch plan details."
          );
        }

        if (planCode && resourceOwnerId) {
          await subscriptionProxy.exists(planCode, resourceOwnerId);
        }
        await checkPaymentMethods();
        planProxy.handleCallbackAction();
      } catch (error) {
        console.error("Error loading product data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    params.code,
    accountSnapshot.userInfo,
    planProxy,
    subscriptionProxy,
    checkPaymentMethods,
    accountProxy
  ]);

  const currentProduct: Plan | undefined = planSnapshot.currentPlan;

  useEffect(() => {
    if (currentProduct) {
      recentProductsStore.addRecentProduct(currentProduct);
    }
  }, [currentProduct, recentProductsStore]);

  const isPlanLoading = isLoading || planSnapshot.currentPlanLoading;
  const isSubscriptionLoading = subscriptionSnapshot.subscriptionLoading;

  return (
    <div className="p-6 pb-16 space-y-6">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogTitle>Subscribe to the plan</DialogTitle>
          <div className="flex flex-col space-y-5">
            Before subscribing to this plan, you neeed to accept the{" "}
            <Link
              className="text-blue-900 underline visited:text-purple-900"
              to={currentProduct?.productEulaLink ?? ""}
              target="_blank"
            >
              EULA
            </Link>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={isTermAccepted}
                onClick={() => setIsTermAccepted(!isTermAccepted)}
              />
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Accept terms and conditions
              </label>
            </div>
          </div>
          <Button disabled={!isTermAccepted} onClick={subscribeHandler}>
            Subscribe
          </Button>
        </DialogContent>
        <div className="px-0">
          <h1 className="text-3xl font-bold tracking-tight">
            {isPlanLoading ? (
              <Skeleton className="inline-block w-64 h-10" />
            ) : !currentProduct ? (
              "Product not found"
            ) : (
              "Product Details"
            )}
          </h1>
          <div className="text-muted-foreground">
            {isPlanLoading ? (
              <Skeleton className="h-5 w-80" />
            ) : !currentProduct ? (
              "We couldn't find the product you were looking for"
            ) : (
              `View and manage details for ${currentProduct.name}`
            )}
          </div>
        </div>
        <CardContent className="p-0 px-0">
          {isPlanLoading ? (
            <div className="flex items-center justify-center py-12">
              <HekaLoader className="w-20 h-20" />
            </div>
          ) : !currentProduct ? (
            <Navigate to="/products" />
          ) : (
            <div className="space-y-6">
              <div className="inline-flex w-full p-5 rounded-md bg-muted">
                <div className="flex flex-col w-4/5">
                  <div className="w-full text-lg font-bold">
                    {currentProduct.name}
                  </div>
                  <div className="w-full font-extralight">
                    {currentProduct.code}
                  </div>
                </div>
                <div className="flex justify-end w-1/5">
                  {isSubscriptionLoading ? (
                    <Button className="w-3/5" disabled>
                      <HekaLoader className="self-center w-full h-full align-baseline" />
                    </Button>
                  ) : !subscriptionSnapshot.subscriptionExists ? (
                    <div className="flex flex-col items-end">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <DialogTrigger asChild>
                              <Button className="flex items-center self-center gap-2">
                                <span>Subscribe to the product</span>
                                {!hasPaymentMethods && (
                                  <CreditCard className="w-4 h-4" />
                                )}
                              </Button>
                            </DialogTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            {hasPaymentMethods
                              ? "Subscribe using your existing payment method"
                              : "You'll be redirected to set up payment method with Stripe"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {!hasPaymentMethods && (
                        <div className="flex items-center mt-2 text-xs text-muted-foreground">
                          <InfoIcon className="w-3 h-3 mr-1" />
                          <span className="align-middle">
                            You'll need to set up a payment method to subscribe
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() =>
                          window.open(currentProduct?.productLink, "_blank")
                        }
                        className="flex self-center gap-2"
                      >
                        <span>Access the product</span>
                        <SquareArrowOutUpRightIcon />
                      </Button>
                      <Button
                        onClick={() =>
                          subscriptionProxy.unsubscribe(
                            currentProduct?.code,
                            accountSnapshot.userInfo?.profile![
                              "urn:zitadel:iam:user:resourceowner:id"
                            ]
                          )
                        }
                        className="self-center"
                      >
                        Unsubscribe
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {!isSubscriptionLoading &&
                !subscriptionSnapshot.subscriptionExists && (
                  <div
                    className={cn(
                      "mt-4 p-4 border border-muted rounded-md flex items-start gap-3 text-sm",
                      hasPaymentMethods === false ? "bg-muted/50" : "hidden"
                    )}
                  >
                    <CreditCard className="w-5 h-5 mt-0.5 text-primary" />
                    <div>
                      <p className="font-medium">Payment Method Required</p>
                      <p className="mt-1 text-muted-foreground">
                        When you click "Subscribe to the product", you'll be
                        redirected to Stripe's secure payment page to add your
                        payment method (credit card or bank account). After
                        completing this step, you'll be returned to continue
                        your subscription.
                      </p>
                      <p className="mt-2 text-muted-foreground">
                        You can also manage your payment methods later from
                        <Button
                          variant="link"
                          className="h-auto p-0 mx-1"
                          onClick={() => accountProxy.setupPaymentMethod()}
                        >
                          Setup your stripe payment method
                        </Button>
                        at any time.
                      </p>
                    </div>
                  </div>
                )}

              <div className="pl-5">
                <label className="font-bold">Description</label>
                <p className="italic">{currentProduct.description}</p>
              </div>
              <Separator />
              {!isSubscriptionLoading &&
                subscriptionSnapshot.subscriptionExists && (
                  <>
                    <div className="pl-5">
                      <label className="font-bold">Product Link</label>
                      <p>
                        <Link
                          className="italic text-blue-900 underline visisted:text-purple-900"
                          to={currentProduct.productLink ?? ""}
                          target="_blank"
                        >
                          {currentProduct.productLink}
                        </Link>
                      </p>
                    </div>
                    <Separator />
                  </>
                )}
              <div className="pl-5">
                <div className="flex justify-between">
                  <label className="font-bold">Charges</label>
                </div>
                {currentProduct.charges.map((charge: any) => (
                  <div className="flex justify-between" key={charge.lago_id}>
                    <label>
                      {charge.billable_metric_code} (
                      {currentProduct?.amount_currency})
                    </label>
                    <label>
                      {charge.properties?.amount} (
                      {charge.properties?.free_units ?? 0} FREE)
                    </label>
                  </div>
                ))}
              </div>
              <Separator />
              <div className="w-full pl-5">
                <div className="flex justify-between">
                  <label>Interval</label>
                  <label>{currentProduct.interval}</label>
                </div>
                <div className="flex justify-between">
                  <label>Currency</label>
                  <label>{currentProduct.amount_currency}</label>
                </div>
                <div className="flex justify-between font-bold">
                  <label>Total</label>
                  <label>{currentProduct.amount_cents}</label>
                </div>
              </div>
              <Separator />
              <div className="pl-5">
                <label className="font-bold">EULA Agreements</label>
                <p>
                  <Link
                    className="text-blue-900 underline visited:text-purple-900"
                    to={currentProduct.productEulaLink ?? ""}
                    target="_blank"
                  >
                    {currentProduct.productEulaLink}
                  </Link>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Dialog>
    </div>
  );
}
